import { useEffect, useMemo, useState } from "react";
import { PiPencilSimpleLine, PiTrash } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import inStockCardIcon from "../../../assets/images/In Stock.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusInStockIcon from "../../../assets/images/Status In Stock.svg";
import statusInstalledIcon from "../../../assets/images/Status Installed.svg";
import statusUnderRepairIcon from "../../../assets/images/Status Under Repair.svg";
import totalTyresCardIcon from "../../../assets/images/Total Tyres.svg";
import tyresUnderRepairCardIcon from "../../../assets/images/Tyres Under Repair.svg";
import AssetIdHoverCell from "../../../components/AssetIdHoverCell";
import AssetSummaryCard from "../../../components/AssetSummaryCard";
import FilterExternalDropdown from "../../../components/FilterExternalDropdown";
import MultiLineText from "../../../components/MultiLineText";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import { fetchVehiclesDropdown } from "../../../store/Slices/dropdownSlice";
import { fetchEquipmentDropdown } from "../../../store/Slices/EquipmentSlice";
import {
    deleteTyre,
    fetchTyreBrandsDropdown,
    fetchTyreData,
    fetchTyresOverview,
} from "../../../store/Slices/TyreSlice";
import {
    AppMainModules,
    AssetsPermission,
    AssetsSubModules,
} from "../../../utils/app-constant";
import { findCollectionByKeys, hasPermission, normalizeOptions } from "../../../utils/helper";
import { activeStatsIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import AssetDeleteModal from "../AssetDeleteModal";

const toText = (value) => String(value ?? "").trim();

const isNullishDisplayValue = (value) => {
  const normalized = toText(value).toLowerCase();
  return (
    !normalized ||
    normalized === "-" ||
    normalized === "--" ||
    normalized === "n/a" ||
    normalized === "null" ||
    normalized === "undefined" ||
    normalized.includes("nan") ||
    normalized.includes("null") ||
    normalized.includes("undefined") ||
    normalized === "eq-n/a" ||
    normalized === "eq--" ||
    normalized === "eq-"
  );
};

const normalizeTyreStatusValue = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (normalizedValue === "installed_on_vehicle") {
    return "installed_vehicle";
  }

  if (normalizedValue === "installed_on_equipment") {
    return "installed_equipment";
  }

  if (normalizedValue === "installed") {
    return "installed";
  }

  return normalizedValue;
};

const toComparableNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const resolvePositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    const normalized = toText(value);
    const trailingDigits = normalized.match(/(\d+)$/);
    if (!trailingDigits) continue;

    const parsedTrailingDigits = Number(trailingDigits[1]);
    if (Number.isFinite(parsedTrailingDigits) && parsedTrailingDigits > 0) {
      return parsedTrailingDigits;
    }
  }

  return null;
};

const formatKm = (value) => `${(toComparableNumber(value) || 0).toLocaleString("en-US")} km`;

const formatDateOnly = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatOverviewDate = (value) => {
  if (!value) return "";

  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatPercent = (part, total) => {
  if (!total) return "0.0%";
  return `${((part / total) * 100).toFixed(1)}%`;
};

const getDefaultTableLimit = () => 10;

const resolveRegistrationNumber = (...values) => {
  for (const value of values) {
    const normalized = toText(value);
    if (!isNullishDisplayValue(normalized)) {
      return normalized;
    }
  }

  return "";
};

const resolveTyreVehicleLabel = (row) => {
  const currentAssetSourceType = toText(row?.current_asset?.source_type).toLowerCase();

  if (currentAssetSourceType === "equipment") {
    return "-";
  }

  const vehicleRegistration = resolveRegistrationNumber(
    row?.current_asset?.vehicle?.registration_number,
    row?.vehicle?.registration_number
  );

  return vehicleRegistration || "-";
};

const resolveTyreEquipmentLabel = (row) => {
  const currentAssetSourceType = toText(row?.current_asset?.source_type).toLowerCase();

  if (currentAssetSourceType === "vehicle") {
    return "-";
  }

  const equipmentRegistration = resolveRegistrationNumber(
    row?.current_asset?.equipment?.registration_number,
    row?.equipment?.registration_number
  );

  return equipmentRegistration || "-";
};

const uniqueMappedOptions = (rows = [], valueAccessor, labelAccessor) => {
  const map = new Map();

  rows.forEach((row) => {
    const rawValue = typeof valueAccessor === "function" ? valueAccessor(row) : "";
    const numericValue = resolvePositiveNumber(rawValue);
    const normalizedValue = numericValue ?? toText(rawValue);
    const uniqueKey = toText(normalizedValue);
    const label = toText(typeof labelAccessor === "function" ? labelAccessor(row) : "");

    if (!uniqueKey || !label || isNullishDisplayValue(label) || map.has(uniqueKey)) return;

    map.set(uniqueKey, {
      label,
      value: numericValue ?? normalizedValue,
    });
  });

  return Array.from(map.values());
};

const hasFilterValue = (value) =>
  !(
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );

const buildSupportedSearchFilters = (filters = {}, rules = {}) => {
  const mapped = {};
  const entries = Object.keys(filters || {}).map((filterName) => [
    filterName,
    rules?.[filterName] || {},
  ]);

  entries.forEach(([filterName, rule = {}]) => {
    const filterObj = filters?.[filterName];
    const rawValue = filterObj?.value;

    if (!filterObj?.filter_type || !hasFilterValue(rawValue)) return;

    const normalizedType =
      typeof rule.normalizeType === "function"
        ? rule.normalizeType(filterObj.filter_type, rawValue)
        : filterObj.filter_type;

    if (!normalizedType) return;

    const normalizedValue =
      typeof rule.normalizeValue === "function"
        ? rule.normalizeValue(rawValue, filterObj.filter_type)
        : rawValue;

    if (!hasFilterValue(normalizedValue)) return;

    mapped[filterName] = {
      filter_type: normalizedType,
      value: normalizedValue,
    };
  });

  return mapped;
};

const normalizeTyreStatusFilterValues = (values = []) =>
  (Array.isArray(values) ? values : [values])
    .map((value) => {
      const normalizedValue = String(value || "").trim().toLowerCase();

      if (
        normalizedValue === "installed" ||
        normalizedValue === "installed_vehicle" ||
        normalizedValue === "installed_equipment"
      ) {
        return "Installed";
      }

      if (normalizedValue === "in_stock") return "In Stock";
      if (normalizedValue === "under_repair") return "Under Repair";
      if (normalizedValue === "inactive") return "Inactive";

      return value;
    })
    .filter(hasFilterValue);

const buildTyreSearchFilters = (filters = {}) =>
  buildSupportedSearchFilters(filters, {
    statusFilter: {
      normalizeValue: (value) => normalizeTyreStatusFilterValues(value),
    },
  });

const mapTyreApiRow = (row) => {
  const systemId = toText(row?.tyres_id || row?.systemId || row?.system_id || row?.id);
  const purchaseDate = formatDateOnly(row?.purchase_at);
  const normalizedTyreStatus = normalizeTyreStatusValue(row?.status || "inactive");
  const currentStatus =
    normalizedTyreStatus === "installed_vehicle" ||
    normalizedTyreStatus === "installed_equipment"
      ? "installed"
      : normalizedTyreStatus;
  const brandId = resolvePositiveNumber(row?.brand?.id, row?.brand_id);
  const brand = toText(row?.brand?.name || row?.brand_name || "");
  const model = toText(row?.model?.name || row?.model_name || "");
  const warehouse = toText(
    row?.warehouse?.name ||
      row?.warehouse?.warehouse_name ||
      (toComparableNumber(row?.warehouse_id) ? `Warehouse #${row.warehouse_id}` : "--")
  );
  const currentAssetSourceType = toText(row?.current_asset?.source_type).toLowerCase();
  const vehicleId =
    currentAssetSourceType === "vehicle"
      ? resolvePositiveNumber(
          row?.current_asset?.vehicle?.id,
          row?.current_asset?.vehicle_id,
          row?.current_asset?.source_id,
          row?.vehicle?.id,
          row?.vehicle_id
        )
      : resolvePositiveNumber(row?.vehicle?.id, row?.vehicle_id);
  const equipmentId =
    currentAssetSourceType === "equipment"
      ? resolvePositiveNumber(
          row?.current_asset?.equipment?.id,
          row?.current_asset?.equipment_id,
          row?.current_asset?.source_id,
          row?.equipment?.id,
          row?.equipment_id
        )
      : resolvePositiveNumber(row?.equipment?.id, row?.equipment_id);
  const linkedVehicleId = resolveTyreVehicleLabel(row);
  const linkedEquipmentId = resolveTyreEquipmentLabel(row);
  const totalKmRunValue = toComparableNumber(row?.odm) || 0;
  const purchaseCost = toComparableNumber(row?.purchase_cost) || 0;
  const capacityValue = toComparableNumber(
    row?.load_capacity ?? row?.maximumLoadCapacity ?? row?.capacity
  );
  const capacity =
    toText(row?.capacity) || (capacityValue !== null ? String(capacityValue) : "");
  const size = toText(row?.size);
  const currentPosition =
    toText(row?.current_position || row?.currentPosition || row?.position);
  const lastInstalledDate =
    formatDateOnly(row?.last_installed_date || row?.installed_at || row?.updated_at) ||
    purchaseDate;

  return {
    ...row,
    id: row?.id,
    systemId,
    tyreId: systemId,
    serialNumber: toText(row?.serial_number),
    brandId,
    brand,
    model,
    capacityValue: capacityValue ?? 0,
    capacity,
    size,
    tyrePattern: toText(row?.tyre_pattern?.name || row?.pattern?.name || ""),
    tyreType: toText(row?.tyre_type?.name || row?.type?.name || ""),
    constructionType: toText(
      row?.construction_type?.name || row?.constructionType?.name || ""
    ),
    initialTreadDepth: toComparableNumber(row?.tread_depth) || 0,
    recommendedPressure: toComparableNumber(row?.pressure) || 0,
    maximumLoadCapacity: toComparableNumber(row?.load_capacity) || 0,
    purchaseDate,
    purchaseCost,
    warehouse,
    currentStatus,
    currentAsset: row?.current_asset || null,
    linkedVehicleRecord: row?.current_asset?.vehicle || row?.vehicle || null,
    vehicleId,
    linkedVehicleId,
    linkedEquipmentRecord: row?.current_asset?.equipment || row?.equipment || null,
    equipmentId,
    linkedEquipmentId,
    currentPosition,
    totalKmRunValue,
    totalKmRun: formatKm(totalKmRunValue),
    lastInstalledDate,
    totalCost: purchaseCost,
    description: row?.description || "",
    notes: row?.notes || "",
    formData: {
      serialNumber: toText(row?.serial_number),
      brand,
      model,
      size,
      tyreSize: size,
      tyrePattern: toText(row?.tyre_pattern?.name || row?.pattern?.name || ""),
      tyreType: toText(row?.tyre_type?.name || row?.type?.name || ""),
      constructionType: toText(
        row?.construction_type?.name || row?.constructionType?.name || ""
      ),
      initialTreadDepth: toComparableNumber(row?.tread_depth) || 0,
      recommendedPressure: toComparableNumber(row?.pressure) || 0,
      maximumLoadCapacity: toComparableNumber(row?.load_capacity) || 0,
      purchaseDate,
      purchaseCost,
      warehouse,
      currentStatus,
      status: currentStatus === "inactive" ? "inactive" : "active",
      description: row?.description || "",
      notes: row?.notes || "",
    },
  };
};

const numericOperatorOptions = [
  { label: "Equal To", value: "equal_to" },
  { label: "Less Than", value: "less_than" },
  { label: "Greater Than", value: "greater_than" },
  { label: "Less Than Equal To", value: "less_than_equal_to" },
  { label: "Greater Than Equal To", value: "greater_than_equal_to" },
];

const TYRE_STATUS_FILTER_OPTIONS = [
  { label: "Installed", value: "installed" },
  { label: "In Stock", value: "in_stock" },
  { label: "Under Repair", value: "under_repair" },
  { label: "Inactive", value: "inactive" },
];

const tyreStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "installed") {
    return {
      color: "#299900",
      label: "Installed",
      icon: statusInstalledIcon,
    };
  }

  if (normalized === "in_stock") {
    return {
      color: "#FF00CC",
      label: "In Stock",
      icon: statusInStockIcon,
    };
  }

  if (normalized === "under_repair") {
    return {
      color: "#F2841B",
      label: "Under Repair",
      icon: statusUnderRepairIcon,
    };
  }

  return {
    color: "#EF0000",
    label: "Inactive",
    icon: inactiveStatusIcon,
  };
};

const appendAssetsRegistrationTab = (path, tab = "vehicles") => {
  const normalizedTab = ["vehicles", "equipments", "tyres"].includes(
    String(tab || "").trim().toLowerCase()
  )
    ? String(tab || "").trim().toLowerCase()
    : "vehicles";
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}tab=${normalizedTab}`;
};

const formatTyreCost = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "--";
  return `PKR ${amount.toLocaleString("en-US")}`;
};

const formatTyreMetric = (value, suffix) => {
  if (isNullishDisplayValue(value)) return "--";
  return `${value} ${suffix}`;
};

const buildTyreHoverPreview = (row) => {
  const detailLine = [row?.brand, row?.model, row?.size]
    .filter((value) => !isNullishDisplayValue(value))
    .map((value, index) => (index === 2 ? `Size ${value}` : value))
    .join(" | ");
  const status = tyreStatusMeta(row?.currentStatus);

  return {
    assetId: row?.tyreId,
    title: row?.serialNumber || row?.tyreId || "--",
    subtitle: detailLine || row?.description || "Tyre Details",
    statusLabel: status.label,
    statusColor: status.color,
    rows: [
      { label: "Warehouse", value: row?.warehouse || "--" },
      {
        label: "Pressure",
        value: formatTyreMetric(row?.formData?.recommendedPressure, "PSI"),
      },
      {
        label: "Tread Depth",
        value: formatTyreMetric(row?.formData?.initialTreadDepth, "mm"),
      },
      { label: "Purchase Cost", value: formatTyreCost(row?.totalCost) },
    ],
  };
};

const AssetsTyresTab = ({
  activeTab = "tyres",
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
  searchFilters: externalSearchFilters,
  setSearchFilters: externalSetSearchFilters,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdateTyre = hasPermission({
    permissionSlug: AssetsPermission.UpdateTyre,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canDeleteTyre = hasPermission({
    permissionSlug: AssetsPermission.DeleteTyre,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const {
    data: tyreApiRows = [],
    loading: tyreLoading = false,
    error: tyreError = null,
    meta: tyreMeta = {},
    deleteTyreLoading = false,
    tyreBrandsDropdownData = [],
    tyreOverviewData = null,
  } = useSelector((state) => state.tyre);
  const vehiclesDropdownData = useSelector(
    (state) =>
      state.dropdowns?.vehiclesDropdownData ||
      state.dropdown?.vehiclesDropdownData ||
      []
  );
  const { equipmentDropdownData = [] } = useSelector(
    (state) => state.equipment
  );

  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalSearchFilters, setInternalSearchFilters] = useState({});

  const searchText =
    externalSearchText !== undefined ? externalSearchText : internalSearchText;
  const setSearchText = externalSetSearchText || setInternalSearchText;
  const searchFilters =
    externalSearchFilters !== undefined
      ? externalSearchFilters
      : internalSearchFilters;
  const setSearchFilters = externalSetSearchFilters || setInternalSearchFilters;
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(getDefaultTableLimit());
  const [removedTyreRows, setRemovedTyreRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const tyreApiSearchFilters = useMemo(
    () => buildTyreSearchFilters(searchFilters),
    [searchFilters]
  );

  useEffect(() => {
    dispatch(fetchTyresOverview());
    dispatch(fetchTyreBrandsDropdown());
    dispatch(fetchVehiclesDropdown());
    dispatch(fetchEquipmentDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(
      fetchTyreData({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: tyreApiSearchFilters,
      })
    );
  }, [currentPage, dispatch, limit, searchText, tyreApiSearchFilters]);

  const tyreBrandDropdownOptions = useMemo(() => {
    const source = Array.isArray(tyreBrandsDropdownData)
      ? tyreBrandsDropdownData
      : findCollectionByKeys(tyreBrandsDropdownData, [
        "tyrebrands",
        "brands",
        "tyrebrandsdropdown",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "brand_id"],
      labelKeys: ["label", "brand_name", "name", "title"],
      defaultLabelPrefix: "Brand",
    });
  }, [tyreBrandsDropdownData]);

  const tyreLinkedVehicleDropdownOptions = useMemo(() => {
    const source = Array.isArray(vehiclesDropdownData)
      ? vehiclesDropdownData
      : findCollectionByKeys(vehiclesDropdownData, [
        "vehicles",
        "vehiclesdropdown",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id"],
      labelKeys: ["label", "registration_number", "name", "title"],
      defaultLabelPrefix: "Vehicle",
    });
  }, [vehiclesDropdownData]);

  const tyreLinkedEquipmentDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentDropdownData)
      ? equipmentDropdownData
      : findCollectionByKeys(equipmentDropdownData, [
        "equipments",
        "equipmentdropdown",
        "equipment",
      ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_id"],
      labelKeys: ["label", "equipment_id", "title", "name", "serial_number"],
      defaultLabelPrefix: "Equipment",
    });
  }, [equipmentDropdownData]);

  const allTyreRows = useMemo(
    () =>
      (Array.isArray(tyreApiRows) ? tyreApiRows : [])
        .map(mapTyreApiRow)
        .filter((row) => row.systemId && !removedTyreRows.includes(row.systemId)),
    [removedTyreRows, tyreApiRows]
  );

  const totalPages = Math.max(1, Number(tyreMeta?.totalPages) || 1);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const tyreStats = useMemo(() => {
    const hasApiOverview = tyreOverviewData && typeof tyreOverviewData === "object";
    const totalTyres = hasApiOverview
      ? Number(tyreOverviewData?.total_tyres) || 0
      : Number(tyreMeta?.totalItems) || allTyreRows.length;
    const installed = hasApiOverview
      ? Number(tyreOverviewData?.installed) || 0
      : allTyreRows.filter((row) => row.currentStatus === "installed").length;
    const inStock = hasApiOverview
      ? Number(tyreOverviewData?.in_stock) || 0
      : allTyreRows.filter((row) => row.currentStatus === "in_stock").length;
    const underRepair = hasApiOverview
      ? Number(tyreOverviewData?.tyres_under_repair) || 0
      : allTyreRows.filter((row) => row.currentStatus === "under_repair").length;
    const lastUpdated = formatOverviewDate(
      hasApiOverview ? tyreOverviewData?.last_updated : ""
    );

    return {
      totalTyres,
      installed,
      inStock,
      underRepair,
      installedPercentage: hasApiOverview
        ? `${Number(tyreOverviewData?.installed_percentage) || 0}%`
        : formatPercent(installed, totalTyres),
      inStockPercentage: hasApiOverview
        ? `${Number(tyreOverviewData?.in_stock_percentage) || 0}%`
        : formatPercent(inStock, totalTyres),
      underRepairPercentage: hasApiOverview
        ? `${Number(tyreOverviewData?.under_repair_percentage) || 0}%`
        : formatPercent(underRepair, totalTyres),
      lastUpdated,
    };
  }, [allTyreRows, tyreMeta?.totalItems, tyreOverviewData]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Tyres",
        value: tyreStats.totalTyres,
        text: <>Last updated : {tyreStats.lastUpdated || "--"}</>,
        icon: (
          <img
            src={totalTyresCardIcon}
            alt="Total tyres"
            className="asset_registration_stats_icon"
          />
        ),
      },
      {
        title: "Installed",
        value: tyreStats.installed,
        text: (
          <span>
            <b>{tyreStats.installedPercentage}</b> from total
          </span>
        ),
        icon: activeStatsIcon,
        className: "completed",
      },
      {
        title: "In Stock",
        value: tyreStats.inStock,
        text: (
          <span>
            <b>{tyreStats.inStockPercentage}</b> from total
          </span>
        ),
        icon: (
          <img
            src={inStockCardIcon}
            alt="In stock"
            className="asset_registration_stats_icon"
          />
        ),
        className: "in-stock",
      },
      {
        title: "Tyres Under Repair",
        value: tyreStats.underRepair,
        text: (
          <span>
            <b>{tyreStats.underRepairPercentage}</b> not operational
          </span>
        ),
        icon: (
          <img
            src={tyresUnderRepairCardIcon}
            alt="Tyres under repair"
            className="asset_registration_stats_icon"
          />
        ),
        className: "pending-delivery",
      },
    ],
    [tyreStats]
  );

  const tyreOptionSets = useMemo(
    () => ({
      brandOptions:
        tyreBrandDropdownOptions.length
          ? tyreBrandDropdownOptions
          : uniqueMappedOptions(allTyreRows, (row) => row.brandId, (row) => row.brand),
      linkedVehicleOptions: tyreLinkedVehicleDropdownOptions.length
        ? tyreLinkedVehicleDropdownOptions
        : uniqueMappedOptions(
          allTyreRows,
          (row) => row.vehicleId,
          (row) => row.linkedVehicleId
        ),
      statusOptions: TYRE_STATUS_FILTER_OPTIONS,
      linkedEquipmentOptions: tyreLinkedEquipmentDropdownOptions.length
        ? tyreLinkedEquipmentDropdownOptions
        : uniqueMappedOptions(
          allTyreRows,
          (row) => row.equipmentId,
          (row) => row.linkedEquipmentId
        ),
    }),
    [
      allTyreRows,
      tyreBrandDropdownOptions,
      tyreLinkedVehicleDropdownOptions,
      tyreLinkedEquipmentDropdownOptions,
    ]
  );

  const handleDeleteRow = async (row) => {
    if (Number(row?.id) > 0) {
      try {
        await dispatch(deleteTyre(row.id)).unwrap();
        successNotify("Tyre deleted successfully");
        dispatch(fetchTyresOverview());

        if (allTyreRows.length === 1 && currentPage > 1) {
          setCurrentPage((previous) => Math.max(1, previous - 1));
        } else {
          dispatch(
            fetchTyreData({
              page: currentPage,
              limit,
              search_text: searchText,
              searchFilters: tyreApiSearchFilters,
            })
          );
        }
      } catch (error) {
        errorNotify(error);
        return false;
      }

      return true;
    }

    setRemovedTyreRows((previous) =>
      previous.includes(row.systemId) ? previous : [...previous, row.systemId]
    );
    return true;
  };

  const closeDeleteModal = () => {
    if (deleteTyreLoading) return;
    setDeleteTarget(null);
  };

  const navigateToLinkedVehicle = (row) => {
    const routeId = row?.vehicleId || toText(row?.linkedVehicleId);
    if (!routeId || isNullishDisplayValue(routeId)) return;

    navigate(
      appendAssetsRegistrationTab(
        `/assets/vehicles/${encodeURIComponent(routeId)}`,
        activeTab
      ),
      {
        state: {
          vehicle: {
            id: row?.vehicleId || null,
            vehicle_id: row?.linkedVehicleId || "",
            systemId: row?.linkedVehicleId || "",
            registrationNumber:
              row?.linkedVehicleRecord?.registration_number || row?.linkedVehicleId || "",
            currentOdometerValue: Number(row?.linkedVehicleRecord?.odm) || 0,
            currentOdometer: row?.linkedVehicleRecord?.odm
              ? `${row.linkedVehicleRecord.odm} km`
              : "",
          },
          activeTab,
        },
      }
    );
  };

  const navigateToLinkedEquipment = (row) => {
    const routeId = row?.equipmentId || toText(row?.linkedEquipmentId);
    if (!routeId || isNullishDisplayValue(routeId)) return;

    navigate(
      appendAssetsRegistrationTab(
        `/assets/equipments/${encodeURIComponent(routeId)}`,
        activeTab
      ),
      {
        state: {
          equipment: {
            id: row?.equipmentId || null,
            systemId: row?.linkedEquipmentId || "",
            equipmentId: row?.linkedEquipmentId || "",
            serialNumber:
              row?.linkedEquipmentRecord?.serial_number || row?.linkedEquipmentId || "",
            registrationNumber:
              row?.linkedEquipmentRecord?.name ||
              row?.linkedEquipmentRecord?.equipment_id ||
              row?.linkedEquipmentId ||
              "",
          },
          activeTab,
        },
      }
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const didDelete = await handleDeleteRow(deleteTarget);
    if (didDelete) {
      setDeleteTarget(null);
    }
  };

  const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
  };

  const columns = [
    {
      ...commonColumns,
      name: "Tyre Id",
      selector: (row, index) => (
        <AssetIdHoverCell
          index={index}
          label={row.tyreId}
          defaultCardWidth={320}
          defaultCardHeight={210}
          onClick={() =>
            navigate(
              appendAssetsRegistrationTab(
                `/assets/tyres/${encodeURIComponent(row.tyreId)}`,
                activeTab
              ),
              {
                state: { tyre: row, activeTab },
              }
            )
          }
        >
          <AssetSummaryCard {...buildTyreHoverPreview(row)} />
        </AssetIdHoverCell>
      ),
      tableColumn: false,
      filterName: "tyreIdFilter",
      type: "text",
      minWidth: "110px",
    },
    {
      ...commonColumns,
      name: "Serial Number",
      selector: (row) => row.serialNumber,
      tableColumn: false,
      filterName: "serialNumberFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "155px",
    },
    {
      ...commonColumns,
      name: "Brand",
      selector: (row) => row.brand,
      tableColumn: false,
      filterName: "brandFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: tyreOptionSets.brandOptions,
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Capacity",
      selector: (row) => row.capacity || "-",
      tableColumn: false,
      filterName: "capacityFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Size",
      selector: (row) => row.size,
      tableColumn: false,
      filterName: "sizeFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "125px",
    },
    {
      ...commonColumns,
      name: "Linked Vehicle",
      selector: (row) => {
        if (isNullishDisplayValue(row?.linkedVehicleId)) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        return (
          <button
            type="button"
            className="assets_equipment_linked_vehicle_chip"
            onClick={() => navigateToLinkedVehicle(row)}
          >
            <span className="service-task">
              <MultiLineText text={row.linkedVehicleId} maxWidth={"90px"} />
            </span>
          </button>
        );
      },
      tableColumn: false,
      filterName: "linkedVehicleIdFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: tyreOptionSets.linkedVehicleOptions,
      minWidth: "175px",
    },

    {
      ...commonColumns,
      name: "Linked Equipment",
      selector: (row) => {
        if (isNullishDisplayValue(row?.linkedEquipmentId)) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        return (
          <button
            type="button"
            className="assets_equipment_linked_vehicle_chip"
            onClick={() => navigateToLinkedEquipment(row)}
          >
            <span className="service-task">
              <MultiLineText text={row.linkedEquipmentId} maxWidth={"90px"} />
            </span>
          </button>
        );
      },
      tableColumn: false,
      filterName: "linkedEquipmentIdFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: tyreOptionSets.linkedEquipmentOptions,
      minWidth: "185px",
    },
    {
      ...commonColumns,
      name: "Status",
      selector: (row) => {
        const meta = tyreStatusMeta(row.currentStatus);

        return (
          <span className="asset_status_cell" style={{ color: meta.color }}>
            <img src={meta.icon} alt={meta.label} className="asset_status_icon" />
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: tyreOptionSets.statusOptions,
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Current Position",
      selector: (row) => row.currentPosition,
      tableColumn: false,
      filterName: "currentPositionFilter",
      type: "text",
      operatorOptions: numericOperatorOptions,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Total KM Run",
      selector: (row) => row.totalKmRun,
      tableColumn: false,
      filterName: "totalKmRunFilter",
      type: "number",
      minWidth: "145px",
    },
    {
      ...commonColumns,
      name: "Last Installed Date",
      selector: (row) => row.lastInstalledDate,
      tableColumn: false,
      filterName: "lastInstalledDateFilter",
      type: "date",
      minWidth: "160px",
    },
    {
      ...commonColumns,
      name: "Total Cost (PKR)",
      selector: (row) => Number(row.totalCost || 0).toLocaleString("en-US"),
      tableColumn: false,
      filterName: "totalCostFilter",
      type: "number",
      minWidth: "150px",
    },
    {
      name: "Action",
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateTyre && (
            <button
              type="button"
              className="edit_btn"
              title="Edit"
              onClick={() =>
                navigate(
                  appendAssetsRegistrationTab(
                    `/assets/tyres/${encodeURIComponent(row.tyreId)}`,
                    activeTab
                  ),
                  {
                    state: { tyre: row, activeTab },
                  }
                )
              }
            >
              <PiPencilSimpleLine />
            </button>
          )}
          {canDeleteTyre && (
            <button
              type="button"
              className="delete_btn"
              title="Delete"
              onClick={() => setDeleteTarget(row)}
            >
              <PiTrash />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <>
      <AssetDeleteModal
        show={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteTyreLoading}
        heading="Delete Tyre"
        message="Are you sure you want to delete this tyre?"
      />

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={allTyreRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={Number(tyreMeta?.totalItems) || allTyreRows.length}
        totalPages={totalPages}
        limitOptions={[10, 20, 50, 100]}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={tyreLoading || deleteTyreLoading}
        paginationLoading={tyreLoading || deleteTyreLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="assets_registration_table assets_registration_table_tyres"
        noDataComponent={
          <div className="table_no_records">
            {tyreError || "There are no records to display"}
          </div>
        }
      />
    </>
  );
};

export default AssetsTyresTab;
